import React from 'react';
import { Box, Card, CircularProgress, Tooltip, Typography, Button, List, ListItem, Checkbox, ModalClose, useTheme } from "@mui/joy";
import { useAuth } from "../context/AuthContext";
import { Stack, useMediaQuery } from "@mui/material";
import { useLanguage } from "../LanguageProvider";
import { Add, CastConnected, Done } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { Transition } from 'react-transition-group';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import DialogTitle from '@mui/joy/DialogTitle';
import DialogContent from '@mui/joy/DialogContent';
import { getApiUrl } from '../utils/api';

function Servers() {
    const { user, loading } = useAuth();
    const [servers, setServers] = useState([]);
    const [loadingServers, setLoading] = useState(true);
    const { changeLanguage, currentLanguageData, language } = useLanguage();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [open, setOpen] = useState(false);
    const [gpu, setGpu] = useState([]);
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            try {
                const response = await fetch(getApiUrl('/v3/ai/servers'), {
                    headers: {
                        'Authorization': `Bearer ${user.jwt_token}`,
                        'Content-Type': 'application/json'
                    }
                });
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const json = await response.json();
                setServers(json.servers);
            } catch (error) {
                console.log(error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);


    if (loading || loadingServers) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: ((window.__TAURI_INTERNALS__ && !isMobile) ? 'calc(100vh - 30px)' : '100vh') }}>
                <CircularProgress color='neutral' />
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flex: 1, justifyContent: 'center' }}>
            {((!user?.ai || (user?.ai?.smart.duration && new Date(user.ai.smart.duration) >= new Date())) && user.ai?.smart?.type == "Enterprise") && (
                <Stack sx={{
                    width: '90%',
                    marginTop: '90px',
                    maxWidth: '740px',
                }}>
                    <Typography sx={{ mb: 1 }} level="title-lg">Your servers</Typography>
                    {servers && Array.isArray(servers) ? (
                        servers.map((server, index) => (
                            <Card key={index} variant="soft" sx={{ mb: 2, borderRadius: 20 }}>
                                <Stack spacing={0.2}>
                                    <Stack sx={{ display: 'flex', alignItems: 'center' }} direction="row" spacing={0.6}>
                                        <Tooltip arrow variant="outlined" placement="bottom" color={server.online ? "success" : "danger"} title={<Typography level="body-xs">Configuration:<br /><Typography sx={{ fontWeight: 'normal' }}>CPU: {server.cpu}<br />GPU: {server.gpu}<br />RAM: {server.ram} GB<br />SSD NVMe: {server.ssd} GB</Typography></Typography>}>
                                            <CastConnected color={server.online ? "success" : "danger"} sx={{ fontSize: 16 }} />
                                        </Tooltip>
                                        <Typography level="body-xs">{server.name}</Typography>
                                    </Stack>
                                    <Typography level="body-xs">Valid until: {user.ai?.smart?.duration ? new Date(user.ai.smart.duration).toLocaleDateString() : 'N/A'}</Typography>
                                    <Box>
                                        <Tooltip arrow variant="outlined" placement="bottom" color="danger" title={<Typography sx={{ fontWeight: 'normal' }} color="danger" level="body-xs">Server management is not available on the starter server.<br />This server is used for your chats together with public servers.<br />To access the panel, purchase a dedicated server separate from your subscription.</Typography>}>
                                            <Button color="danger" variant="soft" sx={{ borderRadius: 20, mt: 1 }} size="sm">
                                                Manage server
                                            </Button>
                                        </Tooltip>
                                    </Box>
                                </Stack>
                            </Card>
                        ))
                    ) : (
                        <Typography>No servers available.</Typography>
                    )}
                    <Box>
                        <Button disabled onClick={() => setOpen(true)} startDecorator={<Add />} variant="soft" sx={{ borderRadius: 250 }}>Build a new server</Button>
                    </Box>
                </Stack>
            )}
            <Transition in={open} timeout={400}>
                {(state) => (
                    <Modal
                        keepMounted
                        open={!['exited', 'exiting'].includes(state)}
                        onClose={() => setOpen(false)}
                        slotProps={{
                            backdrop: {
                                sx: {
                                    opacity: 0,
                                    backdropFilter: 'none',
                                    transition: `opacity 400ms, backdrop-filter 400ms`,
                                    ...{
                                        entering: { opacity: 1, backdropFilter: 'blur(8px)' },
                                        entered: { opacity: 1, backdropFilter: 'blur(8px)' },
                                    }[state],
                                },
                            },
                        }}
                        sx={[
                            state === 'exited'
                                ? { visibility: 'hidden' }
                                : { visibility: 'visible' },
                        ]}
                    >
                        <ModalDialog
                            variant="plain"
                            sx={{
                                opacity: 0,
                                borderRadius: 20,
                                maxWidth: '46vw',
                                transition: `opacity 300ms`,
                                ...{
                                    entering: { opacity: 1 },
                                    entered: { opacity: 1 },
                                }[state],
                            }}
                        >
                            <ModalClose sx={{ borderRadius: 250 }} />
                            <DialogTitle>Configurator</DialogTitle>
                            <DialogContent sx={{ py: 1, px: 0.1 }}>
                                <Stack spacing={1}>
                                    <Stack spacing={0.1}>
                                        <Typography level="title-md">Graphics processing unit</Typography>
                                        <List
                                            orientation="horizontal"
                                            wrap
                                            sx={{
                                                '--List-gap': '8px',
                                                '--ListItem-radius': '20px',
                                                '--ListItem-minHeight': '32px',
                                                '--ListItem-gap': '4px',
                                            }}
                                        >
                                            {['Tesla V100 (32 GB)', 'Tesla V100 (16 GB)', 'Nvidia RTX Quadro 8000 (48 GB)', 'Nvidia A100 (80 GB)', 'Nvidia A100 (40 GB)', 'Nvidia RTX A 5000 (24 GB)', 'Nvidia RTX A 6000 Ada (48 GB)', 'Nvidia RTX 4090 (24 GB)', 'Nvidia RTX 4080 (16 GB)', 'Nvidia RTX 3090 (24 GB)', 'Nvidia RTX 2080 (12 GB)'].map(
                                                (item, index) => (
                                                    <ListItem key={item}>
                                                        {gpu.includes(item) && (
                                                            <Done
                                                                fontSize="md"
                                                                color="primary"
                                                                sx={{ ml: -0.5, zIndex: 2, pointerEvents: 'none' }}
                                                            />
                                                        )}
                                                        <Checkbox
                                                            size="sm"
                                                            disableIcon
                                                            overlay
                                                            label={item}
                                                            checked={gpu.includes(item)}
                                                            variant={gpu.includes(item) ? 'soft' : 'outlined'}
                                                            onChange={(event) => {
                                                                if (event.target.checked) {
                                                                    setGpu((val) => [...val, item]);
                                                                } else {
                                                                    setGpu((val) => val.filter((text) => text !== item));
                                                                }
                                                            }}
                                                            slotProps={{
                                                                action: ({ checked }) => ({
                                                                    sx: checked
                                                                        ? {
                                                                            border: '1px solid',
                                                                            borderColor: 'primary.500',
                                                                        }
                                                                        : {},
                                                                }),
                                                            }}
                                                        />
                                                    </ListItem>
                                                ),
                                            )}
                                        </List>
                                    </Stack>
                                    <Stack spacing={0.1}>
                                        <Typography level="title-md">Central processing unit</Typography>
                                    </Stack>
                                </Stack>
                            </DialogContent>
                        </ModalDialog>
                    </Modal>
                )}
            </Transition>
        </div >
    );
}

export default Servers;
